import * as React from "react";
import { FvcAnalysisSet, FvcSession, FvcTest } from "webportalclient";
import { DateTimeAccuracy, LocalizedDateTime } from "../../../misc/components/LocalizedDateTime";
import { ToggleTestAcceptabilityAction } from "../../Shared/UseAcceptability";
import { AcceptabilityNextGenState } from "../../Shared/UseAcceptabilityNextGen";
import { ToggleTestGraphVisibilityAction } from "../../Shared/UseGraphs";
import { GraphTestData } from "../SpirometryAnalysisScreen";
import { NextGenPftResultParameters, NextGenParameterResult } from "./NextGenPftResultParameters";
import { NextGenQualityAcceptability } from "./NextGenQualityAcceptability";
import { NextGenTestAcceptability } from "./NextGenTestAcceptability";
import { NextGenRepeatabilityTable } from "./NextGenRepeatabilityTable";
import { DynamicResultsGroupHeading } from "../../Shared/ResultsGroupHeading";
import { ParameterType } from "../../../misc/api/ParameterTypes";
import { ResultsContext, useResultsContext } from "../UseResultsContext";
import { NextGenFixedCommentsState } from "./UseNextGenFixedComments";
import { SiteResultsComments } from "./SiteResultComments";
import { ErrorBoundary } from "../../../errors/ErrorBoundary";
import { TopTwoRepeatability } from "./TopTwoRepeatability";

export interface ActiveSpirometryResultsTableProps {
    acceptabilityChangesAllowed: boolean;
    graphData: GraphTestData[];
    analysisSet: FvcAnalysisSet;
    preAnalysisSet?: FvcAnalysisSet;
    session: FvcSession;
    acceptabilityDispatch;
    acceptabilityState: AcceptabilityNextGenState;
    fixedCommentsState: NextGenFixedCommentsState;
    fixedCommentsDispatch;
    graphDispatch;
}

interface ResultsTableHeaderProps {
    graphData: GraphTestData[];
}

const ResultsTableHeader = ({ graphData }: ResultsTableHeaderProps) => {
    const { showBestValues, showPredictedValues, showPercentagePredicted, showReversibilityAbsoluteChange, showReversibilityPercentageChange } = useResultsContext();

    return (
        <thead>
            <tr>
                <th />
                {graphData.sort((a, b) => a.TestNumber - b.TestNumber).map(t => (
                    <th
                        key={`TestHeader${t.TestId}`}
                        style={{ background: t.ColorHex, color: "#fff" }}
                    >
                        {t.TestNumber}
                    </th>
                ))}
                {showBestValues ? <th>Best</th> : null}
                {showReversibilityAbsoluteChange || showReversibilityPercentageChange ? <th>Pre Session Best</th> : null}
                {showReversibilityAbsoluteChange ? <th>Change</th> : null}
                {showReversibilityPercentageChange ? <th>Change %</th> : null}
                {showPredictedValues ? <th>Predicted</th> : null}
                {showPercentagePredicted ? <th>Predicted %</th> : null}
            </tr>
        </thead>
    );
}

const ActiveSpirometryResultsTableCore = ({ acceptabilityChangesAllowed, graphData, analysisSet, preAnalysisSet, session, acceptabilityDispatch, acceptabilityState, fixedCommentsState, fixedCommentsDispatch, graphDispatch }: ActiveSpirometryResultsTableProps) => {
    const { additionalColumnCount } = useResultsContext();

    return (
        <div style={{ overflow: "scroll", maxWidth: "100%" }}>
            <table id="ResultsTable" className="table table-striped">
                <ResultsTableHeader graphData={graphData} />
                <tbody>
                    <tr>
                        <td>Display</td>
                        {graphData.sort((a, b) => a.TestNumber - b.TestNumber).map(t => (
                            <td key={`TestVisibilityToggle${t.TestId}`}>
                                <input
                                    checked={t.IsDisplayed}
                                    type="checkbox"
                                    onChange={() => graphDispatch(ToggleTestGraphVisibilityAction.create(t.TestId))}
                                />
                            </td>
                        ))}
                        <td colSpan={additionalColumnCount - 1} />
                    </tr>
                    <tr>
                        <td>Time</td>
                        {session.tests.sort((a, b) => a.index - b.index).map(t => (
                            <td key={`TestDateTime${t.id}`}>
                                <LocalizedDateTime
                                    time={t.occurred}
                                    accuracy={DateTimeAccuracy.TimeOnly}
                                />
                            </td>
                        ))}
                        <td colSpan={additionalColumnCount - 1} />
                    </tr>
                    <NextGenPftResultParameters
                        session={session}
                        analysisSet={analysisSet}
                    >
                        <>
                            <DynamicResultsGroupHeading resultsCount={analysisSet.tests.length} title="Parameters" />
                            <NextGenParameterResult name="VC (L)" identifier={ParameterType.Vc} value={(t) => t.vc} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="FVC (L)" identifier={ParameterType.Fvc} value={(t) => t.fvc} bestValue={(a) => a.bestFvc} predictedValue={(s) => s.predictedFvc} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="FEV1 (L)"
                                identifier={ParameterType.Fev1}
                                value={(t) => t.fev1}
                                bestValue={(a) => a.bestFev1}
                                reversibilityBaseValue={preAnalysisSet ? preAnalysisSet.bestFev1 : null}
                                reversibilityAbsoluteChange={(a) => a.fev1ReversibilityAbsoluteChange}
                                reversibilityPercentageChange={(a) => a.fev1ReversibilityPercentageChange}
                                predictedValue={(s) => s.predictedFev1}
                                parameterAudience="SelectedTestParameters"
                            />
                            <NextGenParameterResult name="FEV1/VC" identifier={ParameterType.Fev1Vc} value={(t) => t.fev1Vc} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="FEV1 Ratio" identifier={ParameterType.Fev1R} value={(t) => t.fev1R} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="FEV1/FVC" identifier={ParameterType.Fev1Fvc} value={(t) => t.fev1Fvc} bestValue={(a) => a.bestFev1Fvc} predictedValue={(a) => a.predictedFev1Fvc} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="FEV6 (L)" identifier={ParameterType.Fev6} value={(t) => t.fev6} bestValue={(a) => a.bestFev6} predictedValue={(s) => s.predictedFev6} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="PEF (L/sec)" identifier={ParameterType.PefSec} value={(t) => t.pefSec} bestValue={(a) => a.bestPefSec} predictedValue={(s) => s.predictedPefSec} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="PEF (L/min)" identifier={ParameterType.PefMin} value={(t) => t.pefMin} bestValue={(a) => a.bestPefMin} predictedValue={(s) => s.predictedPefMin} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="FIVC (L)" identifier={ParameterType.Fivc} value={(t) => t.fivc} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="PIF (L/sec)" identifier={ParameterType.PifSec} value={(t) => t.pifSec} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="PIF (L/min)" identifier={ParameterType.PifMin} value={(t) => t.pifMin} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="FET (s)" identifier={ParameterType.Fet} value={(t) => t.fet} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="BEV (L)" identifier={ParameterType.Bev} value={(t) => t.bev} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="TV (L)" identifier={ParameterType.Tv} value={(t) => t.tv} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="IRV (L)" identifier={ParameterType.Irv} value={(t) => t.irv} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="ERV (L)" identifier={ParameterType.Erv} value={(t) => t.erv} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="IC (L)" identifier={ParameterType.Ic} value={(t) => t.ic} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="TExt (s)" identifier={ParameterType.Text} value={(t) => t.tExt} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="TPEF (s)" identifier={ParameterType.Tpef} value={(t) => t.tPef} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="FEF 75 (L/s)" identifier={ParameterType.Fef75} value={(t) => t.fef75} bestValue={(s) => s.bestFef75} predictedValue={(s) => s.predictedFef75} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="FEF 25-75 (L/s)" identifier={ParameterType.Fef2575} value={(t) => t.fef2575} bestValue={(a) => a.bestFef2575} predictedValue={(s) => s.predictedFef2575} parameterAudience="SelectedTestParameters" />
                            <NextGenParameterResult name="FEV1/FEV6" identifier={ParameterType.Fev1Fev6} value={(t) => t.fev1Fev6} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedTestParameters" />
                        </>
                    </NextGenPftResultParameters>
                    <TopTwoRepeatability analysisSet={analysisSet} />
                    <NextGenQualityAcceptability session={session} />
                    <NextGenTestAcceptability
                        viewOnly={!acceptabilityChangesAllowed}
                        loading={false}
                        analysisSet={analysisSet}
                        session={session}
                        acceptabilityState={acceptabilityState}
                        onAcceptabilityChange={(id, checked) => acceptabilityDispatch(ToggleTestAcceptabilityAction.create(id, checked))} 
                    />
                    <SiteResultsComments 
                        session={session}
                        fixedCommentsState={fixedCommentsState}
                        fixedCommentsDispatch={fixedCommentsDispatch}
                        acceptabilityChangesAllowed={acceptabilityChangesAllowed}
                    />
                </tbody>
            </table>
        </div>
    );
}

export const ActiveSpirometryResultsTable = (props: ActiveSpirometryResultsTableProps) => {
    const showReversibility = props.session.preSessionId !== undefined && props.session.preSessionId !== null;

    return (
        <ResultsContext.Provider value={{ showBestValues: true, showPredictedValues: true, showPercentagePredicted: true, showReversibilityBaseValue: showReversibility, showReversibilityAbsoluteChange: showReversibility, showReversibilityPercentageChange: showReversibility }}>
            <ErrorBoundary>
                <ActiveSpirometryResultsTableCore {...props} />
            </ErrorBoundary>
        </ResultsContext.Provider>
    );
}
